import currency from "currency.js";

/**
 * Format amount based on company settings
 * @param {number} amount - The amount to format
 * @param {object} settings - Company regional settings
 * @returns {string} - Formatted amount with currency
 */
export const formatCurrency = (amount, settings) => {
  if (!amount && amount !== 0) return "";

  // Provide default settings if settings is undefined
  if (!settings) {
    settings = {
      numberFormat: "comma",
      currencySymbol: "$",
      currencyPosition: "before",
      currencyDisplay: "symbol",
      currency: "USD",
    };
  }

  // Create number formatter based on regional settings
  const numberFormat = getNumberFormat(settings.numberFormat);

  // Format the number
  const formattedAmount = currency(amount, {
    symbol: settings.currencySymbol || "",
    separator: numberFormat.thousands,
    decimal: numberFormat.decimal,
    precision: 2,
    pattern: settings.currencyPosition === "before" ? "!#" : "#!",
  }).format();

  // Handle currency display option
  if (settings.currencyDisplay === "code") {
    return settings.currencyPosition === "before"
      ? `${settings.currency} ${formattedAmount.replace(
          settings.currencySymbol,
          ""
        )}`
      : `${formattedAmount.replace(settings.currencySymbol, "")} ${
          settings.currency
        }`;
  } else if (settings.currencyDisplay === "both") {
    return settings.currencyPosition === "before"
      ? `${settings.currencySymbol} ${formattedAmount.replace(
          settings.currencySymbol,
          ""
        )} ${settings.currency}`
      : `${formattedAmount.replace(settings.currencySymbol, "")} ${
          settings.currencySymbol
        } ${settings.currency}`;
  }

  return formattedAmount;
};

/**
 * Format a number based on company settings (without currency)
 * @param {number} value - The number to format
 * @param {object} settings - Company regional settings
 * @param {number} precision - Number of decimal places
 * @returns {string} - Formatted number
 */
export const formatNumber = (value, settings, precision = 2) => {
  if (!value && value !== 0) return "";

  // Provide default settings if settings is undefined
  if (!settings) {
    settings = {
      numberFormat: "comma",
    };
  }

  const numberFormat = getNumberFormat(settings.numberFormat);

  return currency(value, {
    symbol: "",
    separator: numberFormat.thousands,
    decimal: numberFormat.decimal,
    precision: precision,
  }).format();
};

/**
 * Format a date based on company settings
 * @param {string|Date} date - The date to format
 * @param {object} settings - Company regional settings
 * @returns {string} - Formatted date
 */
export const formatDate = (date, settings) => {
  if (!date) return "";

  // Provide default settings if settings is undefined
  if (!settings) {
    settings = {
      dateFormat: "DD/MM/YYYY",
    };
  }

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return "";

  const day = dateObj.getDate().toString().padStart(2, "0");
  const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
  const monthName = new Intl.DateTimeFormat("en", { month: "short" }).format(
    dateObj
  );
  const year = dateObj.getFullYear();

  switch (settings.dateFormat) {
    case "MM/DD/YYYY":
      return `${month}/${day}/${year}`;
    case "YYYY-MM-DD":
      return `${year}-${month}-${day}`;
    case "DD-MMM-YYYY":
      return `${day}-${monthName}-${year}`;
    case "DD/MM/YYYY":
    default:
      return `${day}/${month}/${year}`;
  }
};

/**
 * Get number format separators based on format setting
 * @param {string} format - Format identifier ('comma', 'dot', 'space')
 * @returns {object} - Object with thousands and decimal separators
 */
const getNumberFormat = (format) => {
  switch (format) {
    case "dot":
      return { thousands: ".", decimal: "," };
    case "space":
      return { thousands: " ", decimal: "," };
    case "comma":
    case undefined:
    case null:
    default:
      return { thousands: ",", decimal: "." };
  }
};

/**
 * Parse formatted number string back to a number
 * @param {string} value - Formatted number string
 * @param {object} settings - Company regional settings
 * @returns {number} - Parsed number
 */
export const parseFormattedNumber = (value, settings) => {
  if (!value) return 0;

  // Provide default settings if settings is undefined
  if (!settings) {
    settings = {
      numberFormat: "comma",
      currencySymbol: "$",
      currency: "USD",
    };
  }

  const numberFormat = getNumberFormat(settings.numberFormat);

  // Remove currency symbol and code if present
  let cleanValue = value.toString();
  if (settings.currencySymbol) {
    cleanValue = cleanValue.replace(settings.currencySymbol, "");
  }
  if (settings.currency) {
    cleanValue = cleanValue.replace(settings.currency, "");
  }

  // Replace separators to parse correctly
  if (numberFormat.thousands !== ",") {
    cleanValue = cleanValue.replace(
      new RegExp("\\" + numberFormat.thousands, "g"),
      "_TEMP_"
    );
    cleanValue = cleanValue.replace(
      new RegExp("\\" + numberFormat.decimal, "g"),
      "."
    );
    cleanValue = cleanValue.replace(/_TEMP_/g, "");
  } else {
    cleanValue = cleanValue.replace(
      new RegExp("\\" + numberFormat.thousands, "g"),
      ""
    );
  }

  // Parse to float
  return parseFloat(cleanValue) || 0;
};

/**
 * Helper function to get currency symbol from currency code
 * @param {string} currencyCode - Currency code (e.g., 'USD', 'EUR')
 * @returns {string} - Currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
  const symbols = {
    AED: "د.إ",
    USD: "$",
    EUR: "€",
    GBP: "£",
    INR: "₹",
    SAR: "﷼",
    JPY: "¥",
    CAD: "C$",
    AUD: "A$",
    CHF: "CHF",
    PKR: "₨", // Pakistan Rupee symbol
    CNY: "¥",
    HKD: "HK$",
    SGD: "S$",
    MYR: "RM",
    THB: "฿",
    IDR: "Rp",
    KRW: "₩",
    BRL: "R$",
    RUB: "₽",
    MXN: "Mex$",
    ZAR: "R",
    TRY: "₺",
    NZD: "NZ$",
    SEK: "kr",
    NOK: "kr",
    DKK: "kr",
  };

  return symbols[currencyCode] || currencyCode;
};

// Country to currency mapping for automatic detection
export const getCountryCurrency = (countryName, countryCode) => {
  // Use country code first, then fall back to name matching
  const code = countryCode?.toUpperCase();
  const name = countryName?.toLowerCase();

  // Comprehensive country-to-currency mapping
  const countryToCurrency = {
    // Major currencies by country code
    AE: { code: "AED", name: "UAE Dirham", symbol: "د.إ" },
    US: { code: "USD", name: "US Dollar", symbol: "$" },
    GB: { code: "GBP", name: "British Pound", symbol: "£" },
    IN: { code: "INR", name: "Indian Rupee", symbol: "₹" },
    SA: { code: "SAR", name: "Saudi Riyal", symbol: "﷼" },
    JP: { code: "JPY", name: "Japanese Yen", symbol: "¥" },
    CA: { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
    AU: { code: "AUD", name: "Australian Dollar", symbol: "A$" },
    CH: { code: "CHF", name: "Swiss Franc", symbol: "CHF" },
    PK: { code: "PKR", name: "Pakistani Rupee", symbol: "₨" },
    CN: { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
    HK: { code: "HKD", name: "Hong Kong Dollar", symbol: "HK$" },
    SG: { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
    MY: { code: "MYR", name: "Malaysian Ringgit", symbol: "RM" },
    TH: { code: "THB", name: "Thai Baht", symbol: "฿" },
    ID: { code: "IDR", name: "Indonesian Rupiah", symbol: "Rp" },
    KR: { code: "KRW", name: "South Korean Won", symbol: "₩" },
    BR: { code: "BRL", name: "Brazilian Real", symbol: "R$" },
    RU: { code: "RUB", name: "Russian Ruble", symbol: "₽" },
    MX: { code: "MXN", name: "Mexican Peso", symbol: "Mex$" },
    ZA: { code: "ZAR", name: "South African Rand", symbol: "R" },
    TR: { code: "TRY", name: "Turkish Lira", symbol: "₺" },
    NZ: { code: "NZD", name: "New Zealand Dollar", symbol: "NZ$" },
    SE: { code: "SEK", name: "Swedish Krona", symbol: "kr" },
    NO: { code: "NOK", name: "Norwegian Krone", symbol: "kr" },
    DKK: { code: "DKK", name: "Danish Krone", symbol: "kr" },

    // European countries using EUR
    AT: { code: "EUR", name: "Euro", symbol: "€" }, // Austria
    BE: { code: "EUR", name: "Euro", symbol: "€" }, // Belgium
    CY: { code: "EUR", name: "Euro", symbol: "€" }, // Cyprus
    EE: { code: "EUR", name: "Euro", symbol: "€" }, // Estonia
    FI: { code: "EUR", name: "Euro", symbol: "€" }, // Finland
    FR: { code: "EUR", name: "Euro", symbol: "€" }, // France
    DE: { code: "EUR", name: "Euro", symbol: "€" }, // Germany
    GR: { code: "EUR", name: "Euro", symbol: "€" }, // Greece
    IE: { code: "EUR", name: "Euro", symbol: "€" }, // Ireland
    IT: { code: "EUR", name: "Euro", symbol: "€" }, // Italy
    LV: { code: "EUR", name: "Euro", symbol: "€" }, // Latvia
    LT: { code: "EUR", name: "Euro", symbol: "€" }, // Lithuania
    LU: { code: "EUR", name: "Euro", symbol: "€" }, // Luxembourg
    MT: { code: "EUR", name: "Euro", symbol: "€" }, // Malta
    NL: { code: "EUR", name: "Euro", symbol: "€" }, // Netherlands
    PT: { code: "EUR", name: "Euro", symbol: "€" }, // Portugal
    SK: { code: "EUR", name: "Euro", symbol: "€" }, // Slovakia
    SI: { code: "EUR", name: "Euro", symbol: "€" }, // Slovenia
    ES: { code: "EUR", name: "Euro", symbol: "€" }, // Spain

    // Additional countries
    EG: { code: "EGP", name: "Egyptian Pound", symbol: "E£" },
    IL: { code: "ILS", name: "Israeli Shekel", symbol: "₪" },
    JO: { code: "JOD", name: "Jordanian Dinar", symbol: "JD" },
    KW: { code: "KWD", name: "Kuwaiti Dinar", symbol: "KD" },
    LB: { code: "LBP", name: "Lebanese Pound", symbol: "L£" },
    OM: { code: "OMR", name: "Omani Rial", symbol: "OMR" },
    QA: { code: "QAR", name: "Qatari Rial", symbol: "QR" },
    BH: { code: "BHD", name: "Bahraini Dinar", symbol: "BD" },
    IR: { code: "IRR", name: "Iranian Rial", symbol: "﷼" },
    IQ: { code: "IQD", name: "Iraqi Dinar", symbol: "ID" },
    AF: { code: "AFN", name: "Afghan Afghani", symbol: "؋" },
    BD: { code: "BDT", name: "Bangladeshi Taka", symbol: "৳" },
    BT: { code: "BTN", name: "Bhutanese Ngultrum", symbol: "Nu." },
    LK: { code: "LKR", name: "Sri Lankan Rupee", symbol: "Rs" },
    MV: { code: "MVR", name: "Maldivian Rufiyaa", symbol: "Rf" },
    NP: { code: "NPR", name: "Nepalese Rupee", symbol: "Rs" },
    MM: { code: "MMK", name: "Myanmar Kyat", symbol: "K" },
    LA: { code: "LAK", name: "Lao Kip", symbol: "₭" },
    KH: { code: "KHR", name: "Cambodian Riel", symbol: "៛" },
    VN: { code: "VND", name: "Vietnamese Dong", symbol: "₫" },
    PH: { code: "PHP", name: "Philippine Peso", symbol: "₱" },
    TW: { code: "TWD", name: "Taiwan Dollar", symbol: "NT$" },
    MN: { code: "MNT", name: "Mongolian Tugrik", symbol: "₮" },
    UZ: { code: "UZS", name: "Uzbekistani Som", symbol: "лв" },
    KZ: { code: "KZT", name: "Kazakhstani Tenge", symbol: "₸" },
    KG: { code: "KGS", name: "Kyrgyzstani Som", symbol: "лв" },
    TJ: { code: "TJS", name: "Tajikistani Somoni", symbol: "SM" },
    TM: { code: "TMT", name: "Turkmenistani Manat", symbol: "T" },
    AZ: { code: "AZN", name: "Azerbaijani Manat", symbol: "₼" },
    AM: { code: "AMD", name: "Armenian Dram", symbol: "֏" },
    GE: { code: "GEL", name: "Georgian Lari", symbol: "₾" },
    UA: { code: "UAH", name: "Ukrainian Hryvnia", symbol: "₴" },
    BY: { code: "BYN", name: "Belarusian Ruble", symbol: "Br" },
    MD: { code: "MDL", name: "Moldovan Leu", symbol: "L" },
    RO: { code: "RON", name: "Romanian Leu", symbol: "lei" },
    BG: { code: "BGN", name: "Bulgarian Lev", symbol: "лв" },
    RS: { code: "RSD", name: "Serbian Dinar", symbol: "Дин." },
    BA: {
      code: "BAM",
      name: "Bosnia-Herzegovina Convertible Mark",
      symbol: "KM",
    },
    HR: { code: "HRK", name: "Croatian Kuna", symbol: "kn" },
    CZ: { code: "CZK", name: "Czech Koruna", symbol: "Kč" },
    HU: { code: "HUF", name: "Hungarian Forint", symbol: "Ft" },
    PL: { code: "PLN", name: "Polish Zloty", symbol: "zł" },
    IS: { code: "ISK", name: "Icelandic Krona", symbol: "kr" },
    AL: { code: "ALL", name: "Albanian Lek", symbol: "L" },
    MK: { code: "MKD", name: "Macedonian Denar", symbol: "ден" },
    ME: { code: "EUR", name: "Euro", symbol: "€" }, // Montenegro uses EUR
    CS: { code: "EUR", name: "Euro", symbol: "€" }, // Kosovo uses EUR
    MA: { code: "MAD", name: "Moroccan Dirham", symbol: "MAD" },
    DZ: { code: "DZD", name: "Algerian Dinar", symbol: "DA" },
    TN: { code: "TND", name: "Tunisian Dinar", symbol: "DT" },
    LY: { code: "LYD", name: "Libyan Dinar", symbol: "LD" },
    SD: { code: "SDG", name: "Sudanese Pound", symbol: "ج.س." },
    ET: { code: "ETB", name: "Ethiopian Birr", symbol: "Br" },
    KE: { code: "KES", name: "Kenyan Shilling", symbol: "KSh" },
    UG: { code: "UGX", name: "Ugandan Shilling", symbol: "USh" },
    TZ: { code: "TZS", name: "Tanzanian Shilling", symbol: "TSh" },
    RW: { code: "RWF", name: "Rwandan Franc", symbol: "R₣" },
    GH: { code: "GHS", name: "Ghanaian Cedi", symbol: "₵" },
    NG: { code: "NGN", name: "Nigerian Naira", symbol: "₦" },
    CI: { code: "XOF", name: "West African CFA Franc", symbol: "CFA" },
    SN: { code: "XOF", name: "West African CFA Franc", symbol: "CFA" },
    CM: { code: "XAF", name: "Central African CFA Franc", symbol: "FCFA" },
    ZW: { code: "USD", name: "US Dollar", symbol: "$" }, // Zimbabwe uses USD
    BW: { code: "BWP", name: "Botswana Pula", symbol: "P" },
    ZM: { code: "ZMW", name: "Zambian Kwacha", symbol: "ZK" },
    MZ: { code: "MZN", name: "Mozambican Metical", symbol: "MT" },
    MG: { code: "MGA", name: "Malagasy Ariary", symbol: "Ar" },
    MU: { code: "MUR", name: "Mauritian Rupee", symbol: "₨" },
    SC: { code: "SCR", name: "Seychellois Rupee", symbol: "₨" },
    AR: { code: "ARS", name: "Argentine Peso", symbol: "$" },
    CL: { code: "CLP", name: "Chilean Peso", symbol: "$" },
    CO: { code: "COP", name: "Colombian Peso", symbol: "$" },
    PE: { code: "PEN", name: "Peruvian Sol", symbol: "S/." },
    EC: { code: "USD", name: "US Dollar", symbol: "$" }, // Ecuador uses USD
    VE: { code: "VES", name: "Venezuelan Bolívar", symbol: "Bs" },
    UY: { code: "UYU", name: "Uruguayan Peso", symbol: "$U" },
    PY: { code: "PYG", name: "Paraguayan Guarani", symbol: "₲" },
    BO: { code: "BOB", name: "Bolivian Boliviano", symbol: "Bs" },
    CR: { code: "CRC", name: "Costa Rican Colón", symbol: "₡" },
    GT: { code: "GTQ", name: "Guatemalan Quetzal", symbol: "Q" },
    HN: { code: "HNL", name: "Honduran Lempira", symbol: "L" },
    NI: { code: "NIO", name: "Nicaraguan Córdoba", symbol: "C$" },
    PA: { code: "USD", name: "US Dollar", symbol: "$" }, // Panama uses USD
    SV: { code: "USD", name: "US Dollar", symbol: "$" }, // El Salvador uses USD
    BZ: { code: "BZD", name: "Belize Dollar", symbol: "BZ$" },
    JM: { code: "JMD", name: "Jamaican Dollar", symbol: "J$" },
    TT: { code: "TTD", name: "Trinidad and Tobago Dollar", symbol: "TT$" },
    BB: { code: "BBD", name: "Barbadian Dollar", symbol: "Bds$" },
    GY: { code: "GYD", name: "Guyanese Dollar", symbol: "G$" },
    SR: { code: "SRD", name: "Surinamese Dollar", symbol: "$" },
  };

  // First try to match by country code
  if (code && countryToCurrency[code]) {
    return countryToCurrency[code];
  }

  // Fallback: match by country name
  const nameMapping = {
    "united arab emirates": countryToCurrency["AE"],
    uae: countryToCurrency["AE"],
    emirates: countryToCurrency["AE"],
    "united states": countryToCurrency["US"],
    usa: countryToCurrency["US"],
    america: countryToCurrency["US"],
    "united kingdom": countryToCurrency["GB"],
    uk: countryToCurrency["GB"],
    britain: countryToCurrency["GB"],
    england: countryToCurrency["GB"],
    india: countryToCurrency["IN"],
    "saudi arabia": countryToCurrency["SA"],
    saudi: countryToCurrency["SA"],
    japan: countryToCurrency["JP"],
    canada: countryToCurrency["CA"],
    australia: countryToCurrency["AU"],
    switzerland: countryToCurrency["CH"],
    pakistan: countryToCurrency["PK"],
    china: countryToCurrency["CN"],
    "hong kong": countryToCurrency["HK"],
    singapore: countryToCurrency["SG"],
    malaysia: countryToCurrency["MY"],
    thailand: countryToCurrency["TH"],
    indonesia: countryToCurrency["ID"],
    "south korea": countryToCurrency["KR"],
    korea: countryToCurrency["KR"],
    brazil: countryToCurrency["BR"],
    russia: countryToCurrency["RU"],
    mexico: countryToCurrency["MX"],
    "south africa": countryToCurrency["ZA"],
    turkey: countryToCurrency["TR"],
    "new zealand": countryToCurrency["NZ"],
    sweden: countryToCurrency["SE"],
    norway: countryToCurrency["NO"],
    denmark: countryToCurrency["DK"],
    france: countryToCurrency["FR"],
    germany: countryToCurrency["DE"],
    italy: countryToCurrency["IT"],
    spain: countryToCurrency["ES"],
    netherlands: countryToCurrency["NL"],
    belgium: countryToCurrency["BE"],
    austria: countryToCurrency["AT"],
    portugal: countryToCurrency["PT"],
    ireland: countryToCurrency["IE"],
    greece: countryToCurrency["GR"],
    finland: countryToCurrency["FI"],
    poland: countryToCurrency["PL"],
    "czech republic": countryToCurrency["CZ"],
    hungary: countryToCurrency["HU"],
    romania: countryToCurrency["RO"],
    bulgaria: countryToCurrency["BG"],
    croatia: countryToCurrency["HR"],
    ukraine: countryToCurrency["UA"],
    egypt: countryToCurrency["EG"],
    israel: countryToCurrency["IL"],
    jordan: countryToCurrency["JO"],
    kuwait: countryToCurrency["KW"],
    lebanon: countryToCurrency["LB"],
    oman: countryToCurrency["OM"],
    qatar: countryToCurrency["QA"],
    bahrain: countryToCurrency["BH"],
    iran: countryToCurrency["IR"],
    iraq: countryToCurrency["IQ"],
    afghanistan: countryToCurrency["AF"],
    bangladesh: countryToCurrency["BD"],
    "sri lanka": countryToCurrency["LK"],
    nepal: countryToCurrency["NP"],
    philippines: countryToCurrency["PH"],
    vietnam: countryToCurrency["VN"],
    taiwan: countryToCurrency["TW"],
    mongolia: countryToCurrency["MN"],
    kazakhstan: countryToCurrency["KZ"],
    uzbekistan: countryToCurrency["UZ"],
    kyrgyzstan: countryToCurrency["KG"],
    tajikistan: countryToCurrency["TJ"],
    turkmenistan: countryToCurrency["TM"],
    azerbaijan: countryToCurrency["AZ"],
    armenia: countryToCurrency["AM"],
    georgia: countryToCurrency["GE"],
    belarus: countryToCurrency["BY"],
    moldova: countryToCurrency["MD"],
    serbia: countryToCurrency["RS"],
    "bosnia and herzegovina": countryToCurrency["BA"],
    macedonia: countryToCurrency["MK"],
    albania: countryToCurrency["AL"],
    montenegro: countryToCurrency["ME"],
    iceland: countryToCurrency["IS"],
    morocco: countryToCurrency["MA"],
    algeria: countryToCurrency["DZ"],
    tunisia: countryToCurrency["TN"],
    libya: countryToCurrency["LY"],
    sudan: countryToCurrency["SD"],
    ethiopia: countryToCurrency["ET"],
    kenya: countryToCurrency["KE"],
    uganda: countryToCurrency["UG"],
    tanzania: countryToCurrency["TZ"],
    rwanda: countryToCurrency["RW"],
    ghana: countryToCurrency["GH"],
    nigeria: countryToCurrency["NG"],
    zimbabwe: countryToCurrency["ZW"],
    botswana: countryToCurrency["BW"],
    zambia: countryToCurrency["ZM"],
    mozambique: countryToCurrency["MZ"],
    madagascar: countryToCurrency["MG"],
    mauritius: countryToCurrency["MU"],
    seychelles: countryToCurrency["SC"],
    argentina: countryToCurrency["AR"],
    chile: countryToCurrency["CL"],
    colombia: countryToCurrency["CO"],
    peru: countryToCurrency["PE"],
    ecuador: countryToCurrency["EC"],
    venezuela: countryToCurrency["VE"],
    uruguay: countryToCurrency["UY"],
    paraguay: countryToCurrency["PY"],
    bolivia: countryToCurrency["BO"],
    "costa rica": countryToCurrency["CR"],
    guatemala: countryToCurrency["GT"],
    honduras: countryToCurrency["HN"],
    nicaragua: countryToCurrency["NI"],
    panama: countryToCurrency["PA"],
    "el salvador": countryToCurrency["SV"],
    belize: countryToCurrency["BZ"],
    jamaica: countryToCurrency["JM"],
    "trinidad and tobago": countryToCurrency["TT"],
    barbados: countryToCurrency["BB"],
    guyana: countryToCurrency["GY"],
    suriname: countryToCurrency["SR"],
  };

  if (name && nameMapping[name]) {
    return nameMapping[name];
  }

  // Default fallback to USD if no match found
  return { code: "USD", name: "US Dollar", symbol: "$" };
};
